import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useAccessOwner } from './access-owner-provider'
import { accessSwitchFailureMessage, openAccessPath } from './open-access-path'

/**
 * Returns a function that opens the domain or organization a path names. It resolves to whether
 * the path was opened. A failure is shown to the user and the page stays as it was, so the result
 * never rejects and a caller has no rejection to forget. A call that a newer navigation superseded
 * resolves to false with nothing shown: the newer navigation reports its own outcome.
 *
 * `route` navigates to a path. The caller supplies it because only the caller knows whether a
 * router push or a full page load is right (a new app version may be waiting). `notify` shows an
 * error message to the user; the caller supplies it so this module stays free of the UI layer.
 */
export function useOpenAccessPath(
    route: (path: string) => void,
    notify: (message: string) => void,
): (path: string) => Promise<boolean> {
    const history = useHistory()
    const owner = useAccessOwner()

    return useCallback(
        async (path: string) => {
            const result = await openAccessPath(
                owner,
                {
                    pathname: () => history.location.pathname,
                    push: route,
                },
                path,
            )
            if (result.ok) {
                return true
            }
            if (!result.superseded) {
                notify(accessSwitchFailureMessage(result.kind))
            }
            return false
        },
        [history, owner, route, notify],
    )
}
